import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Menu } from "lucide-react";
import ConfirmationDialog from "../../../Components/ConfirmationDialogs/ConfirmationDialogs";
import UploadExcel from "../../../Components/UploadExcel";
import { normalizeRowKeys } from "../../../utils/case";
import { slugify } from "../../../utils/slug";
import SearchBar from "../../../Components/DataView/Searchbar";
import Pagination from "../../../Components/DataView/Pagination";
import { useFirestorePagination } from "../../../hooks/useFirestorePagination";
import { useSearchBar } from "../../../hooks/useSearchBar";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { useDelete } from "../../../hooks/useDelete";

/* ---------- types and helper functions (adapted from your original) ---------- */
interface UniversityListingItem {
  ID?: string;
  universityName: string;
  logo?: string;
  intro?: string;
  keyInfo?: string[];
  rankings?: string[];
  campus?: any[];
  country?: string;
  degreeIntake?: string[];
  diplomaIntake?: string[];
  foundationIntake?: string[];
  masterIntake?: string[];
  courses?: string[];
  strengthsHighlights?: string[];
  slug?: string;
  universityLocation?: string;
}

const transformRow = (row: Record<string, any>) => {
  const src = { ...normalizeRowKeys(row) };
  const toArray = (val: any, fieldName?: string) => {
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      if (fieldName === "Strengths & Highlights") {
        const matches = val.match(/\[([^\]]+)\]/g);
        if (matches) {
          return matches
            .map((s: string) => s.slice(1, -1).trim())
            .filter(Boolean);
        }
        return val.trim() ? [val.trim()] : [];
      } else {
        return val
          .split(";")
          .map((s: string) => s.trim())
          .filter(Boolean);
      }
    }
    return [];
  };
  const universityName: string = src.universityName ?? "";
  return {
    universityName,
    slug: slugify(universityName),
    intro: src.intro ?? "",
    keyInfo: toArray(src.keyInfo),
    rankings: toArray(src.rankings),
    campus: toArray(src.campus),
    degreeIntake: toArray(src.degreeIntake),
    diplomaIntake: toArray(src.diplomaIntake),
    foundationIntake: toArray(src.foundationIntake),
    masterIntake: toArray(src.masterIntake),
    courses: toArray(src.courses),
    strengthsHighlights: toArray(
      src.strengthsHighlights,
      "Strengths & Highlights",
    ),
    logo: src.logo ?? "",
    universityLocation: src.universityLocation ?? src.country ?? "",
  };
};

const getDeduplicationKey = (row: Record<string, any>) => {
  const data = row as any;
  const name: string = data.universityName || "";
  const slug: string = data.slug || slugify(name);
  return slug ? String(slug).trim().toLowerCase() : "";
};

const isDuplicate = (
  row: Record<string, any>,
  existingDocs: Map<string, any>,
) => {
  const slug = getDeduplicationKey(row);
  if (!slug) return false;
  return existingDocs.has(slug);
};

/* ----------------- Component ----------------- */
export default function UniversityListing() {
  const navigate = useNavigate();
  const { userRole } = useAuthUser();

  // Sorting
  const [sortField, setSortField] = useState("universityName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Search mode state
  const [isSearchMode, setIsSearchMode] = useState(false);

  const itemsPerPage = 20;

  // Hooks
  const {
    items: currentUniversities,
    currentPage,
    totalPages,
    showPage,
    refresh,
    setItems,
    setTotalDocs,
    setTotalPages,
    setCurrentPage,
    setIsLoading,
  } = useFirestorePagination<UniversityListingItem>({
    collectionName: "university-listing",
    itemsPerPage,
    sortField,
    sortOrder,
    isSearchMode,
  });

  const {
    searchBarRef,
    handleSearchResults,
    onLoading: onSearchLoading,
  } = useSearchBar(
    {
      setItems,
      setTotalDocs,
      setTotalPages,
      setCurrentPage,
      setIsLoading,
    },
    itemsPerPage,
    isSearchMode,
    setIsSearchMode,
  );

  // UI state (dropdown + delete dialog)
  const [dropdownUniversityId, setDropdownUniversityId] = useState<
    string | null
  >(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  /* ----------------- scroll-to-top after page change ----------------- */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  /* ----------------- click outside dropdown ----------------- */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-dropdown-menu]")) {
        setDropdownUniversityId(null);
        setDropdownPosition(null);
        setDropdownAnchor(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ----------------- Actions ----------------- */
  const handleAddUniversity = () =>
    navigate("/university-listing/add-university");

  const {
    showDeleteDialog,
    confirmDelete,
    cancelDelete,
    handleDelete: handleDeleteUniversity,
    itemToDelete: universityToDelete,
  } = useDelete<UniversityListingItem>({
    collectionName: "university-listing",
    itemNameField: "universityName",
    onSuccess: refresh,
  });

  /* ----------------- Sorting header click ----------------- */
  const handleSortHeaderClick = (field: string) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  /* ----------------- Render ----------------- */
  return (
    <div className="relative flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">University Listing</h2>
        <div className="flex gap-4">
          <UploadExcel
            collectionName="university-listing"
            transformRow={transformRow}
            isDuplicate={isDuplicate}
            getDeduplicationKey={getDeduplicationKey}
            buttonLabel="Upload University"
          />
          <button
            onClick={handleAddUniversity}
            className="w-32 bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700"
          >
            Add University
          </button>
        </div>
      </div>

      <SearchBar
        ref={searchBarRef}
        placeholder="Search university name..."
        collectionName="university-listing"
        searchFields={["universityName", "slug"]}
        onResults={handleSearchResults}
        onLoading={onSearchLoading}
      />

      {/* Table */}
      <div className="relative min-h-[80%] overflow-x-auto border rounded mb-6">
        <table className="w-full text-sm text-center">
          <thead>
            <tr className="bg-gray-300 sticky top-0 z-10">
              <th className="p-1">No</th>
              {[
                { label: "University Name", field: "universityName" },
                { label: "Intro", field: "intro" },
                { label: "Key Info", field: "keyInfo" },
                { label: "Rankings", field: "rankings" },
                { label: "Campus", field: "campus" },
                { label: "Degree Intake", field: "degreeIntake" },
                { label: "Diploma Intake", field: "diplomaIntake" },
                { label: "Foundation Intake", field: "foundationIntake" },
                { label: "Master Intake", field: "masterIntake" },
              ].map(({ label, field }) => (
                <th
                  key={field}
                  className="p-1 cursor-pointer select-none"
                  onClick={() => handleSortHeaderClick(field)}
                >
                  {label}
                  {sortField === field && (
                    <span className="ml-1">
                      {sortOrder === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
              ))}
              <th className="p-1">Action</th>
            </tr>
          </thead>
          <tbody>
            {!currentUniversities || currentUniversities.length === 0 ? (
              <tr>
                <td colSpan={12} className="p-2">
                  No university listings found.
                </td>
              </tr>
            ) : (
              currentUniversities.map((university, index) => (
                <tr
                  key={university.ID || index}
                  className="hover:bg-gray-100 border-b"
                >
                  <td className="p-1">
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </td>
                  <td className="p-1">{university.universityName}</td>
                  <td className="p-1">
                    {(university.intro || "").split("\n")[0].slice(0, 40)}
                  </td>
                  <td className="p-1">
                    {Array.isArray(university.keyInfo)
                      ? university.keyInfo.join("; ")
                      : ""}
                  </td>
                  <td className="p-1">
                    {Array.isArray(university.rankings)
                      ? university.rankings.join("; ")
                      : ""}
                  </td>
                  <td className="p-1">
                    {Array.isArray(university.campus)
                      ? university.campus
                          .map((c: any) =>
                            typeof c === "object" && c !== null
                              ? c.location
                              : typeof c === "string"
                                ? c
                                : "",
                          )
                          .filter(Boolean)
                          .join(", ")
                      : ""}
                  </td>
                  <td className="p-1">
                    {Array.isArray(university.degreeIntake)
                      ? university.degreeIntake.join("; ")
                      : ""}
                  </td>
                  <td className="p-1">
                    {Array.isArray(university.diplomaIntake)
                      ? university.diplomaIntake.join("; ")
                      : ""}
                  </td>
                  <td className="p-1">
                    {Array.isArray(university.foundationIntake)
                      ? university.foundationIntake.join("; ")
                      : ""}
                  </td>
                  <td className="p-1">
                    {Array.isArray(university.masterIntake)
                      ? university.masterIntake.join("; ")
                      : ""}
                  </td>
                  <td className="p-1">
                    <button
                      onClick={(e) => {
                        const el = e.currentTarget as HTMLElement;
                        const rect = el.getBoundingClientRect();
                        setDropdownUniversityId(university.ID || null);
                        setDropdownAnchor(el);
                        setDropdownPosition({
                          top: rect.bottom,
                          left: rect.left,
                        });
                      }}
                      className="text-gray-700 hover:text-black focus:outline-none"
                      title="Actions"
                    >
                      <Menu className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={(page) => showPage(page)}
      />

      {/* Action Dropdown */}
      {dropdownUniversityId && dropdownPosition && (
        <div
          data-dropdown-menu
          className="fixed z-10 w-32 bg-white border rounded shadow"
          style={{
            top: dropdownPosition.top - 30,
            left: dropdownPosition.left - 140,
          }}
        >
          <button
            onClick={() => {
              navigate(
                `/university-listing/edit-university/${dropdownUniversityId}`,
              );
              setDropdownUniversityId(null);
              setDropdownPosition(null);
              setDropdownAnchor(null);
            }}
            className="block w-full text-left px-4 py-1 hover:bg-gray-100"
          >
            Edit
          </button>
          {userRole === "super admin" && (
            <button
              onClick={() => {
                const found = currentUniversities.find(
                  (u) => u.ID === dropdownUniversityId,
                );
                if (found) {
                  confirmDelete(found);
                }
                setDropdownUniversityId(null);
                setDropdownPosition(null);
                setDropdownAnchor(null);
              }}
              className="block w-full text-left px-4 py-1 text-red-600 hover:bg-gray-100"
            >
              Delete
            </button>
          )}
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        icon={<Trash2 size={32} className="text-red-600" />}
        title="Delete University"
        message={`Are you sure you want to delete ${universityToDelete?.universityName}? This action cannot be undone.`}
        onCancel={cancelDelete}
        onConfirm={handleDeleteUniversity}
      />
    </div>
  );
}
